import React, { useEffect, useState } from 'react'
import { get } from 'lodash'
import { useHistory }  from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AdminCommonListLayout from './AdminCommonListLayout'
import {
  adminGalleriesList, adminGalleriesProcessingList, adminGalleriesFacialRecognitionList,
  adminGalleriesBibRecognitionList, adminGalleriesProcessGalleryPhotographsList
} from '../actions/admin_galleries'
import { showSuccess } from '../../actions/Error'
import {
  makeStyles, TextField, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Grid, Typography, Menu, MenuItem
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import CloseIcon from '@material-ui/icons/Close'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import RefreshIcon from '@material-ui/icons/Refresh'

const useStyles = makeStyles((theme) => ({
  gallery_image: {
      maxWidth: '120px',
  },
  searchBox: {
    minWidth: '350px',
  }
}))

export const AdminGalleries = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const is_loading = useSelector(() => adminGalleriesList.isLoading())
  const [galleryNameSearch, setGalleryNameSearch] = useState('')
  const [albumSearch, setAlbumSearch] = useState('')
  const [eventSearch, setEventSearch] = useState('')
  const [photographerSearch, setPhotographerSearch] = useState('')
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedGallery, setSelectedGallery] = useState(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [menuGallery, setMenuGallery] = useState(null)
  const [checkProcessingDialogOpen, setCheckProcessingDialogOpen] = useState(false)
  const [checkProcessingGallery, setCheckProcessingGallery] = useState(null)
  const [checkProcessingData, setCheckProcessingData] = useState(null)
  const [checkProcessingLoading, setCheckProcessingLoading] = useState(false)

  const columns = [
    {
      field: 'gallery_name',
      title: 'Gallery Name',
    },
    {
      field: 'album_name',
      title: 'Album',
    },
    {
      field: 'gallery_event_name',
      title: 'Gallery Event',
    },
    {
      field: 'photographer_name',
      title: 'Photographer',
    },
    {
      field: 'num_photographs',
      title: 'Num Photos',
    },
    {
      field: 'can_index_for_facial_recognition',
      title: 'FR Selected',
      render: (item) => {
        return item.can_index_for_facial_recognition ? 'Yes' : 'No'
      }
    },
    {
      field: 'indexed_for_facial_recognition',
      title: 'FR Indexed',
      render: (item) => {
        return item.indexed_for_facial_recognition ? 'Yes' : 'No'
      }
    },
    {
      field: 'commission_split',
      title: 'Commission Split',
      render: (item) => {
        const commission = item.event_commission_percentage
        return commission != null ? `${commission}%` : '-'
      }
    },
    {
      field: 'created_at',
      title: 'Created At',
      render: (item) => {
        const date = new Date(item.created_at)
        return date.toLocaleString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
      }
    },
    {
      field: 'view',
      title: 'View',
      render: (item) => (
        <div>
          <button onClick={() => handleViewGallery(item)}>
            View
          </button>
        </div>
      )
    },
    {
      field: 'actions',
      title: 'Actions',
      render: (item) => (
        <div>
          <IconButton
            aria-label="more"
            aria-controls="gallery-actions-menu"
            aria-haspopup="true"
            onClick={(e) => handleMenuOpen(e, item)}
          >
            <MoreVertIcon />
          </IconButton>
        </div>
      )
    },
  ]

  useEffect(() => {
    async function fetchInitialGalleries() {
      // Load just 10 galleries for fast initial display
      // More will be loaded only when user interacts with pagination controls
      // Clear any search filters and reset pagination
      setGalleryNameSearch('')
      setAlbumSearch('')
      setEventSearch('')
      setPhotographerSearch('')
      dispatch(adminGalleriesList.updateListFilter({
        gallery_name: '',
        album_name: '',
        event_name: '',
        photographer_name: ''
      }))
      dispatch(adminGalleriesList.updateListPagination({items_per_page: 10, page: 1}))
      await dispatch(adminGalleriesList.fetchListIfNeeded(true))
    }
    fetchInitialGalleries()
  }, [])

  const onAddGallery = () => {
    history.push('/admin/gallery/')
  }

  const onEditGallery = (gallery_id) => {
    history.push(`/admin/gallery/${ gallery_id }`)
  }

  const handleViewGallery = (gallery) => {
    setSelectedGallery(gallery)
    setViewDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setViewDialogOpen(false)
    setSelectedGallery(null)
  }

  const handleMenuOpen = (event, gallery) => {
    setMenuAnchorEl(event.currentTarget)
    setMenuGallery(gallery)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuGallery(null)
  }

  const handleCopyGalleryUrl = () => {
    const gallery = menuGallery
    const galleryDbId = get(gallery, "db_id")
    const gallerySessionName = get(gallery, "session_name", "").split(' ').join('-')
    const customerOrigin = window.location.origin.replace('://admin.', '://www.')
    const galleryUrl = `${customerOrigin}/customer/gallery/${gallerySessionName}/${galleryDbId}`

    navigator.clipboard
      .writeText(galleryUrl)
      .then(() => showSuccess("Success", "Gallery URL copied to clipboard"))
    handleMenuClose()
  }

  const handleRunFacialRecognition = () => {
    const gallery = menuGallery
    const galleryId = get(gallery, "id")
    dispatch(adminGalleriesFacialRecognitionList.index({galleryId}))
    showSuccess("Running")
    handleMenuClose()
  }

  const handleIndexBibRecognition = () => {
    const gallery = menuGallery
    const galleryId = get(gallery, "id")
    dispatch(adminGalleriesBibRecognitionList.index({galleryId}))
    showSuccess("Indexing")
    handleMenuClose()
  }

  const handleProcessGalleryPhotographs = () => {
    const gallery = menuGallery
    const galleryId = get(gallery, "id")
    dispatch(adminGalleriesProcessGalleryPhotographsList.process({galleryId}))
    showSuccess("Processing Gallery Photographs")
    handleMenuClose()
  }

  const handleUnpublishGallery = () => {
    const gallery = menuGallery
    const values = {
      id: get(gallery, "id"),
      available_to_purchase: false
    }
    dispatch(adminGalleriesList.saveObject(values)).then((json) => {
      if (json.errors) {
        return
      }
      dispatch(adminGalleriesList.invalidateList())
      dispatch(adminGalleriesList.fetchListIfNeeded())
      showSuccess("Success", "Gallery unpublished")
    })
    handleMenuClose()
  }

  const handlePublishGallery = () => {
    const gallery = menuGallery
    const values = {
      id: get(gallery, "id"),
      available_to_purchase: true
    }
    dispatch(adminGalleriesList.saveObject(values)).then((json) => {
      if (json.errors) {
        return
      }
      dispatch(adminGalleriesList.invalidateList())
      dispatch(adminGalleriesList.fetchListIfNeeded())
      showSuccess("Success", "Gallery published")
    })
    handleMenuClose()
  }

  const handleCheckProcessing = async () => {
    const gallery = menuGallery
    setCheckProcessingGallery(gallery)
    setCheckProcessingDialogOpen(true)
    setCheckProcessingData(null)
    setCheckProcessingLoading(true)
    handleMenuClose()

    const data = await dispatch(adminGalleriesProcessingList.check({galleryId: gallery.id}))
    setCheckProcessingData(data)
    setCheckProcessingLoading(false)
  }

  const handleCloseCheckProcessingDialog = () => {
    setCheckProcessingDialogOpen(false)
    setCheckProcessingGallery(null)
    setCheckProcessingData(null)
  }

  const handleRefreshCheckProcessing = async () => {
    if (!checkProcessingGallery) return
    setCheckProcessingData(null)
    setCheckProcessingLoading(true)
    const data = await dispatch(adminGalleriesProcessingList.check({galleryId: checkProcessingGallery.id}))
    setCheckProcessingData(data)
    setCheckProcessingLoading(false)
  }

  const handleSearch = (fieldName, value, setterFunction) => {
    setterFunction(value)

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    // Only search if there are at least 3 characters, or if clearing the search
    if (value.length >= 3 || value.length === 0) {
      // Debounce: wait 500ms after user stops typing before searching
      const timeout = setTimeout(() => {
        const currentFilters = {
          gallery_name: galleryNameSearch,
          album_name: albumSearch,
          event_name: eventSearch,
          photographer_name: photographerSearch,
          [fieldName]: value
        }
        // Ensure pagination stays at 10 items per page during search
        dispatch(adminGalleriesList.updateListPagination({items_per_page: 10, page: 1}))
        dispatch(adminGalleriesList.updateListFilter(currentFilters))
        dispatch(adminGalleriesList.fetchListIfNeeded(true))
      }, 500)
      setSearchTimeout(timeout)
    }
  }

  const renderColumnSearchBoxes = () => {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 150px',
        gap: '8px',
        marginBottom: '10px',
        width: '100%',
        paddingRight: '48px' // Account for table actions column
      }}>
        <TextField
          placeholder="Gallery name..."
          value={galleryNameSearch}
          onChange={(e) => handleSearch('gallery_name', e.target.value, setGalleryNameSearch)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: galleryNameSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearch('gallery_name', '', setGalleryNameSearch)}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          placeholder="Album..."
          value={albumSearch}
          onChange={(e) => handleSearch('album_name', e.target.value, setAlbumSearch)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: albumSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearch('album_name', '', setAlbumSearch)}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          placeholder="Event..."
          value={eventSearch}
          onChange={(e) => handleSearch('event_name', e.target.value, setEventSearch)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: eventSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearch('event_name', '', setEventSearch)}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          placeholder="Photographer..."
          value={photographerSearch}
          onChange={(e) => handleSearch('photographer_name', e.target.value, setPhotographerSearch)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: photographerSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearch('photographer_name', '', setPhotographerSearch)}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <div /> {/* Empty space for Num Photos column */}
        <div /> {/* Empty space for FR Selected column */}
        <div /> {/* Empty space for FR Indexed column */}
        <div /> {/* Empty space for Created At column */}
      </div>
    )
  }

    return (
      <>
        <AdminCommonListLayout
          active_key='galleries'
          breadcrumbs={[
            { name: 'admin_home' },
            {
              name: 'galleries',
              label: 'Galleries',
              url: '/admin/galleries',
            },
          ]}
          columns={ columns }
          is_loading={ is_loading }
          item_list={ adminGalleriesList }
          renderAdditionalActions={ renderColumnSearchBoxes }
        />

        <Dialog open={viewDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Gallery Details
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              style={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedGallery && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {selectedGallery.gallery_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Gallery ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.primary_key || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Password
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.gallery_password || 'None'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Album
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.album_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Gallery Event
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.gallery_event_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Photographer
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.photographer_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Photographer Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.photographer_email || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedGallery.created_at).toLocaleString('en-GB', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Shot At
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.shot_at_datetime ? new Date(selectedGallery.shot_at_datetime).toLocaleString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                      timeZone: 'UTC'
                    }) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Available to Purchase
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.available_to_purchase ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Indexed for Facial Recognition
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.indexed_for_facial_recognition ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Indexed for Bib Recognition
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.indexed_for_bib_number_recognition === 'indexed' ? 'Yes' : selectedGallery.indexed_for_bib_number_recognition === 'partial' ? 'Partial' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Deleted
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.deleted_at ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Can Index for Facial Recognition
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.can_index_for_facial_recognition ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Gallery Event Logo
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.add_gallery_event_logo ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Custom Event Logo
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.custom_gallery_event_logo ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Base Price
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.base_price ?? 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Event Commission Percentage
                  </Typography>
                  <Typography variant="body1">
                    {selectedGallery.event_commission_percentage != null
                      ? `${selectedGallery.event_commission_percentage}%`
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary" variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          id="gallery-actions-menu"
          anchorEl={menuAnchorEl}
          keepMounted
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleCopyGalleryUrl}>
            Copy Gallery Url
          </MenuItem>
          <MenuItem onClick={handleRunFacialRecognition}>
            Run Facial Recognition
          </MenuItem>
          <MenuItem onClick={handleIndexBibRecognition}>
            Index Bib Recognition
          </MenuItem>
          <MenuItem onClick={handleProcessGalleryPhotographs}>
            Process Gallery Photographs
          </MenuItem>
          <MenuItem onClick={handleUnpublishGallery}>
            Unpublish Gallery
          </MenuItem>
          <MenuItem onClick={handlePublishGallery}>
            Publish Gallery
          </MenuItem>
          <MenuItem onClick={handleCheckProcessing}>
            Check Processing
          </MenuItem>
        </Menu>

        <Dialog open={checkProcessingDialogOpen} onClose={handleCloseCheckProcessingDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {checkProcessingGallery?.gallery_name || 'Gallery'}
            <IconButton
              aria-label="close"
              onClick={handleCloseCheckProcessingDialog}
              style={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {checkProcessingGallery && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Num Photos
                  </Typography>
                  <Typography variant="body1">
                    {checkProcessingGallery.num_photographs}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Actual Num Photos
                  </Typography>
                  <Typography variant="body1">
                    {checkProcessingLoading ? '...' : (checkProcessingData?.actual_num_photos ?? '-')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Photos Processed
                  </Typography>
                  <Typography variant="body1">
                    {checkProcessingLoading ? '...' : (checkProcessingData?.photos_processed ?? '-')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    FR Indexed
                  </Typography>
                  <Typography variant="body1">
                    {checkProcessingLoading ? '...' : (checkProcessingData?.fr_indexed ?? '-')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Bib Indexed
                  </Typography>
                  <Typography variant="body1">
                    {checkProcessingLoading ? '...' : (checkProcessingData?.bib_indexed ?? '-')}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleRefreshCheckProcessing}
              color="default"
              variant="outlined"
              disabled={checkProcessingLoading}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
            <Button onClick={handleCloseCheckProcessingDialog} color="primary" variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
}
